import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Zap, Plus, Edit2, Trash2, Building2, Power } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SmartPlugManagement({ userRole = 'admin' }) {
  const [plugs, setPlugs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPlug, setSelectedPlug] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plugsRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/plugs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/branches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setPlugs(plugsRes.data || []);
      if (branchesRes.data.success) {
        setBranches(branchesRes.data.branches);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (plug) => {
    setSelectedPlug(plug);
    setShowEditModal(true);
  };

  const handleDeleteClick = (plug) => {
    setSelectedPlug(plug);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedPlug) return;

    try {
      await axios.delete(`${API_URL}/api/plugs/${selectedPlug.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      toast.success('Smart plug berhasil dihapus!', {
        description: 'Data smart plug telah dihapus dari sistem',
        duration: 3000
      });
    } catch (error) {
      toast.error('Gagal menghapus smart plug', {
        description: error.response?.data?.detail || 'Terjadi kesalahan saat menghapus smart plug',
        duration: 4000
      });
    } finally {
      setSelectedPlug(null);
    }
  };

  const getBranchName = (plug) => {
    // Try branch_id first
    if (plug.branch_id) {
      const branch = branches.find(b => b.branch_id === plug.branch_id);
      if (branch) return branch.name;
    }
    // Fallback to branch name
    if (plug.branch) return plug.branch;
    return 'Belum di-assign';
  };

  return (
    <div className="billing-smartplug-management space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Management Smart Plug</h2>
          <p className="text-sm text-gray-600">Kelola smart plug dan assignment ke cabang</p>
        </div>
        {/* Hide Add button for Supervisor */}
        {userRole === 'admin' && (
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Tambah Smart Plug
          </Button>
        )}
      </div>

      {/* Plugs Grid */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plugs.map((plug) => (
            <Card key={plug.id} className="billing-smartplug-card">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-600" />
                    <CardTitle className="text-lg">{plug.name}</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditClick(plug)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(plug)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {plug.ps_type && (
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      plug.ps_type === 'PS5' ? 'bg-purple-100 text-purple-700' :
                      plug.ps_type === 'PS4' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {plug.ps_type}
                    </span>
                    {plug.price_per_hour && (
                      <span className="text-sm font-bold text-orange-600">
                        Rp {plug.price_per_hour.toLocaleString('id-ID')}/jam
                      </span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700 font-medium">
                    {getBranchName(plug)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Power className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600 text-xs">
                    {plug.smartthings_device_id?.slice(0, 20)}...
                  </span>
                </div>
                {(!plug.branch_id && !plug.branch) && (
                  <div className="mt-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded text-center">
                     Belum di-assign ke cabang
                  </div>
                )}
                {(!plug.ps_type || !plug.price_per_hour) && (
                  <div className="mt-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded text-center">
                     Harga belum di-set
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddPlugModal
          branches={branches}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
          token={token}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPlug && (
        <EditPlugModal
          plug={selectedPlug}
          branches={branches}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlug(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedPlug(null);
            fetchData();
          }}
          token={token}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedPlug(null);
        }}
        onConfirm={handleDeleteConfirmed}
        title="Konfirmasi Hapus Smart Plug"
        description={`Apakah Anda yakin ingin menghapus smart plug "${selectedPlug?.name}"?`}
        confirmText="Ya, Hapus"
        cancelText="Batal"
        variant="danger"
      />
    </div>
  );
}

// Add Plug Modal Component
function AddPlugModal({ branches, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    name: '',
    smartthings_device_id: '',
    branch_id: '',
    branch: '',
    ps_type: '',
    price_per_hour: ''
  });
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSmartThingsDevices();
  }, []);

  const fetchSmartThingsDevices = async () => {
    setLoadingDevices(true);
    try {
      const response = await axios.get(`${API_URL}/api/plug/smartthings/devices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDevices(response.data.devices || []);
        if ((response.data.devices || []).length === 0 && response.data.message) {
          setError(response.data.message);
        }
      } else {
        setError(response.data.error || response.data.message || 'Gagal memuat daftar device SmartThings');
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError(error.response?.data?.detail || 'Gagal memuat daftar device');
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/plugs`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data) {
        onSuccess();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Gagal menambahkan smart plug');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Tambah Smart Plug Baru</DialogTitle>
          <DialogDescription>
            Pilih device dari SmartThings dan atur assignment cabang
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="device">SmartThings Device *</Label>
            {loadingDevices ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-600">Memuat devices...</span>
              </div>
            ) : devices.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                <p className="font-semibold mb-1">Tidak ada device ditemukan</p>
                <p className="text-xs">Pastikan device SmartThings Anda sudah terhubung dan memiliki capability "switch".</p>
              </div>
            ) : (
              <select
                id="device"
                value={formData.smartthings_device_id}
                onChange={(e) => {
                  const selectedDevice = devices.find(d => d.device_id === e.target.value);
                  setFormData({
                    ...formData,
                    smartthings_device_id: e.target.value,
                    name: formData.name || selectedDevice?.name || ''
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">-- Pilih Device --</option>
                {devices.map(device => (
                  <option key={device.device_id} value={device.device_id}>
                    {device.name} ({device.manufacturer || 'Unknown'})
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Device akan di-detect otomatis dari SmartThings API
            </p>
          </div>

          <div>
            <Label htmlFor="name">Nama PS Unit *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Contoh: PS 1, PS 2"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Nama untuk identifikasi unit PS
            </p>
          </div>

          <div>
            <Label htmlFor="ps_type">Tipe PS *</Label>
            <select
              id="ps_type"
              value={formData.ps_type}
              onChange={(e) => setFormData({...formData, ps_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">-- Pilih Tipe PS --</option>
              <option value="PS3">PS3</option>
              <option value="PS4">PS4</option>
              <option value="PS5">PS5</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Tipe PlayStation untuk unit ini
            </p>
          </div>

          <div>
            <Label htmlFor="price_per_hour">Harga Per Jam (Rp) *</Label>
            <Input
              id="price_per_hour"
              type="number"
              value={formData.price_per_hour}
              onChange={(e) => setFormData({...formData, price_per_hour: e.target.value})}
              placeholder="Contoh: 12000"
              required
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Harga rental per jam untuk unit ini
            </p>
          </div>

          <div>
            <Label htmlFor="branch">Cabang (Optional)</Label>
            <select
              id="branch"
              value={formData.branch_id}
              onChange={(e) => {
                const selectedBranch = branches.find(b => b.branch_id === e.target.value);
                setFormData({
                  ...formData, 
                  branch_id: e.target.value,
                  branch: selectedBranch?.name || ''
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Belum di-assign (bisa di-set nanti)</option>
              {branches.map(branch => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Cabang bisa di-set sekarang atau nanti via Edit
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button 
              type="submit" 
              disabled={loading || loadingDevices || !formData.smartthings_device_id} 
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Menyimpan...' : 'Tambah Smart Plug'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit Plug Modal Component
function EditPlugModal({ plug, branches, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    name: plug.name || '',
    branch: plug.branch || '',
    branch_id: plug.branch_id || '',
    ps_type: plug.ps_type || '',
    price_per_hour: plug.price_per_hour || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.put(
        `${API_URL}/api/plugs/${plug.id}`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data) {
        onSuccess();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Gagal update smart plug');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Smart Plug</DialogTitle>
          <DialogDescription>
            Update assignment cabang untuk smart plug
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nama Smart Plug</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Contoh: PS Unit 1"
              required
            />
          </div>

          <div>
            <Label htmlFor="ps_type">Tipe PS *</Label>
            <select
              id="ps_type"
              value={formData.ps_type}
              onChange={(e) => setFormData({...formData, ps_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">-- Pilih Tipe PS --</option>
              <option value="PS3">PS3</option>
              <option value="PS4">PS4</option>
              <option value="PS5">PS5</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Tipe saat ini: {plug.ps_type || 'Belum di-set'}
            </p>
          </div>

          <div>
            <Label htmlFor="price_per_hour">Harga Per Jam (Rp) *</Label>
            <Input
              id="price_per_hour"
              type="number"
              value={formData.price_per_hour}
              onChange={(e) => setFormData({...formData, price_per_hour: e.target.value})}
              placeholder="Contoh: 12000"
              required
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Harga saat ini: {plug.price_per_hour ? `Rp ${plug.price_per_hour.toLocaleString('id-ID')}` : 'Belum di-set'}
            </p>
          </div>

          <div>
            <Label htmlFor="branch">Cabang *</Label>
            <select
              id="branch"
              value={formData.branch_id}
              onChange={(e) => {
                const selectedBranch = branches.find(b => b.branch_id === e.target.value);
                setFormData({
                  ...formData, 
                  branch_id: e.target.value,
                  branch: selectedBranch?.name || ''
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Pilih Cabang</option>
              {branches.map(branch => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Cabang saat ini: {plug.branch || 'Belum di-assign'}
            </p>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg text-sm text-gray-700">
            <p className="font-semibold mb-1">Device ID (SmartThings):</p>
            <p className="text-xs font-mono break-all">{plug.smartthings_device_id || 'Belum terhubung'}</p>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
